import React from 'react';
import Box from '@mui/material/Box';
import DateRangeFilter from './DateRangeFilter';
import { parseISO } from 'date-fns';
import { useFilterContext } from '../context/FilterContext';
import { useParams, useLocation } from 'react-router-dom';
import { BrandsFilter } from './BrandsFilter';
import { PlatformsFilter } from './PlatformsFilter';
import { PillarsFilter } from './PillarsFilter';
import { CircularProgress } from '@mui/material';

// Export this function for use in other components
export function formatDate(date: Date | null): string | undefined {
  if (!date) return undefined;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

const FilterSet: React.FC = () => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const location = useLocation();

  // Use the filter context
  const {
    selectedBrands,
    selectedPlatforms,
    selectedPillars,
    startDate,
    endDate,
    setSelectedBrands,
    setSelectedPlatforms,
    setSelectedPillars,
    setStartDate,
    setEndDate,
    updateUrlParams,
    dataDateRange,
    dashboardType,
    isInitialized
  } = useFilterContext();

  // Determine if on personal metrics page for employee brand dashboard
  const isPersonalMetricsPage = dashboardType === 'personal_branding' && location.pathname.endsWith('/personal');

  // Handle date changes
  const handleDateChange = (date: Date | null, isStartDate: boolean) => {
    const updatedStartDate = isStartDate ? date : startDate;
    const updatedEndDate = isStartDate ? endDate : date;

    // Update state
    if (isStartDate) {
      setStartDate(date);
    } else {
      setEndDate(date);
    }

    // Always update URL with both parameters, using defaults if needed
    const formattedStartDate = formatDate(updatedStartDate);
    const formattedEndDate = formatDate(updatedEndDate || new Date());
    
    // Create an object with the parameters to update
    const dateParams: Record<string, string | undefined> = {};
    if (formattedStartDate) {
      dateParams.start_date = formattedStartDate;
    }
    if (formattedEndDate) {
      dateParams.end_date = formattedEndDate;
    }
    
    // Update URL parameters
    updateUrlParams(dateParams);
  };

  // Handle brand selection with URL update
  const handleBrandsChange = (selected: string[]) => {
    setSelectedBrands(selected);
    updateUrlParams({ brands: selected });
  };

  // Handle platform selection with URL update
  const handlePlatformsChange = (selected: string[]) => {
    setSelectedPlatforms(selected);
    updateUrlParams({ platforms: selected });
  };

  // Handle pillar selection with URL update
  const handlePillarsChange = (selected: string[]) => {
    setSelectedPillars(selected);
    
    // Special handling for pillars
    const params: Record<string, string[]> = {};
    if (selected.length > 0) {
      params.pillars = selected;
    }
    updateUrlParams(params);
  };

  // Show loading state if context is not yet initialized
  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, py: 1, color: 'text.secondary' }}>
        <CircularProgress size={18} /> Loading filters...
      </Box>
    );
  }

  const dateFilter = (
    <DateRangeFilter
      startDate={startDate}
      endDate={endDate}
      onChange={(newStartDate, newEndDate) => {
        if (newStartDate !== startDate) handleDateChange(newStartDate, true);
        if (newEndDate !== endDate) handleDateChange(newEndDate, false);
      }}
      minDate={dataDateRange.start || undefined}
      maxDate={dataDateRange.end || undefined}
    />
  );

  const brandsFilter = (
    <BrandsFilter
      orgId={orgId}
      selectedBrands={selectedBrands}
      onBrandsChange={handleBrandsChange}
      dashboardType={dashboardType}
      isPersonalMetricsPage={isPersonalMetricsPage}
    />
  );

  const pillarsFilter = (
    <PillarsFilter
      orgId={orgId}
      selectedPillars={selectedPillars}
      onPillarsChange={handlePillarsChange}
    />
  );

  // One wrapping row of compact controls. The date range is the widest, the
  // selects share the rest and wrap onto a second line on narrow screens.
  return (
    <Box
      role="group"
      aria-label="Dashboard filters"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: { lg: 'flex-end' } }}
    >
      <Box sx={{ flex: '1 1 270px', maxWidth: { sm: 340 } }}>{dateFilter}</Box>
      <Box sx={{ flex: '1 1 170px' }}>{brandsFilter}</Box>
      {dashboardType !== 'personal_branding' && (
        <Box sx={{ flex: '1 1 170px' }}>
          <PlatformsFilter
            orgId={orgId}
            selectedPlatforms={selectedPlatforms}
            onPlatformsChange={handlePlatformsChange}
          />
        </Box>
      )}
      <Box sx={{ flex: '1 1 170px' }}>{pillarsFilter}</Box>
    </Box>
  );
};

export default FilterSet; 